/* eslint-disable @typescript-eslint/naming-convention */
import React, { useContext, useEffect, useState } from 'react';
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TablePagination,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import SellIcon from '@mui/icons-material/Sell';
import { makeStyles } from 'tss-react/mui';

import CustomerTableHead from './CustomerTableHead';
import CustomerHeaderToolbar from './CustomerHeaderToolbar';
import CustomerMenu from '../Components/CustomerMenu';
import CommonTag from '../../../Common/Tag/CommonTag';
import { Loader } from '../../../../Common/Components/Loader';

import { Customer, CustomerModule, EnvType } from '../Model/CustomerModel';
import { Tenant } from '../../ManageFarms/Model/FarmModel';
import { TagDetails } from '../../../Common/CommonModel';
import { EnvOption, EnvironmentOptionDto } from '../Model/CustomerProps';

import { customerService } from '../CustomerService';
import { tenantService } from '../../Tenant/TenantService';
import { globalService } from '../../UIContainer/Global/GlobalService';

import {
  getSorting,
  stableSort,
  getLocalDate,
} from '../../../../Helpers/UtilityMethods';
import { Order } from '../../../../Helpers/Literals';
import { PermissionsContext } from '../../UIContainer/PermissionsWrapper';
import PermissionConstants from '../../../Common/Constants/PermissionConstants';
import CustomerConstants from '../Model/CustomerConstants';

import { showToast } from '../../../../lib/toast';
import {
  getDeploymentStatusChip,
  getEnvChip,
} from '../../Common/CustomerChips';

/* -------------------- Styles -------------------- */

const useStyles = makeStyles()((theme: Theme) => ({
  root: { width: '100%' },
  paper: { width: '100%', marginBottom: theme.spacing(2) },
  table: { minWidth: 750 },
  tableWrapper: { overflowX: 'auto' },
}));

/* -------------------- Constants -------------------- */

const ROWS_PER_PAGE = 10;

/* -------------------- Component -------------------- */

export default function CustomerHomePage() {
  const { classes } = useStyles();
  const permissions = useContext(PermissionsContext);

  /* -------------------- State -------------------- */

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersCopy, setCustomersCopy] = useState<Customer[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customerModules, setCustomerModules] = useState<
    Record<string, string[]>
  >({});
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Customer>('Subdomain');

  const [searchText, setSearchText] = useState('');
  const [collaborationOnly, setCollaborationOnly] = useState(false);
  const [selectedEnvTypes, setSelectedEnvTypes] = useState<EnvType[]>([]);
  const [selectedCustomerTags, setSelectedCustomerTags] = useState<string[]>(
    [],
  );

  const [customerTagDetails, setCustomerTagDetails] = useState<TagDetails[]>(
    [],
  );
  const [customerTags, setCustomerTags] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [envOptions, setEnvOptions] = useState<EnvOption[]>([]);

  const [showTags, setShowTags] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  /* -------------------- Data Loading -------------------- */

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);

      const [
        customersRes,
        modulesRes,
        envRes,
        tenantsRes,
        tagDetailsRes,
        tagsRes,
      ] = await Promise.all([
        customerService.getCustomers(),
        customerService.getAllCustomerModules(),
        customerService.getEnvironmentOptions(),
        tenantService.getTenants(),
        globalService.getTagDetailsByObjectType('customer'),
        globalService.getTags(),
      ]);

      setCustomers(customersRes.Items);
      setCustomersCopy(customersRes.Items);
      setTenants(tenantsRes.Items);

      setEnvOptions(
        envRes.map((dto: EnvironmentOptionDto) => ({
          value: dto.Value,
          name: dto.Name,
        })),
      );

      const moduleMap: Record<string, string[]> = {};
      modulesRes.Items.forEach((m: CustomerModule) => {
        moduleMap[m.CustomerID] ??= [];
        moduleMap[m.CustomerID].push(m.ModuleID);
      });

      setCustomerModules(moduleMap);
      setCustomerTagDetails(tagDetailsRes.Items);
      setCustomerTags([
        ...new Set<string>(
          tagDetailsRes.Items.map((x: TagDetails) => x.TagName),
        ),
      ]);
      setTagOptions(tagsRes.Items.map((x: { TagName: string }) => x.TagName));
    } catch (error) {
      console.error(error);
      showToast({ type: 'error', title: 'Failed to load customers' });
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Filtering -------------------- */

  useEffect(() => {
    let filtered = customersCopy;

    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter((c) =>
        [c.CustomerName, c.Subdomain, c.Id]
          .join(' ')
          .toLowerCase()
          .includes(lower),
      );
    }

    if (collaborationOnly) {
      filtered = filtered.filter((c) =>
        customerModules[c.Id]?.includes('collaboration'),
      );
    }

    if (selectedCustomerTags.length) {
      const matchedIds = customerTagDetails
        .filter((x) => selectedCustomerTags.includes(x.TagName))
        .map((x) => x.ObjectId);
      filtered = filtered.filter((c) => matchedIds.includes(c.Id));
    }

    if (selectedEnvTypes.length) {
      filtered = filtered.filter((c) => selectedEnvTypes.includes(c.EnvType));
    }

    setCustomers(filtered);
  }, [
    searchText,
    collaborationOnly,
    selectedEnvTypes,
    selectedCustomerTags,
    customersCopy,
  ]);

  /* -------------------- Derived Data -------------------- */

  const getOnboardDate = (customerId: string): string => {
    const tenant = tenants.find((t) => t.Id === customerId);
    return tenant?.OnboardedDate ? getLocalDate(tenant.OnboardedDate) : '--';
  };

  const getTenantVersion = (customerId: string): string => {
    const tenant = tenants.find((t) => t.Id === customerId);
    return tenant?.ProVersion ?? '--';
  };

  const canViewCustomers = (): boolean =>
    !!(
      permissions[PermissionConstants.CustomerReader] ||
      permissions[PermissionConstants.CustomerWriter]
    );

  /* -------------------- Handlers -------------------- */

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Customer,
  ) => {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  };

  const handleTagIconClick = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setShowTags(true);
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  /* -------------------- Render -------------------- */

  const sortedPage = stableSort(customers, getSorting(order, orderBy)).slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  return (
    <div className={classes.root}>
      <CustomerHeaderToolbar
        onFilterRequest={(collab, e) => {
          setSearchText(e.target.value);
          setCollaborationOnly(collab);
        }}
        onCollaborationOnlyFilter={(search, collab) => {
          setSearchText(search);
          setCollaborationOnly(collab);
        }}
        onAdd={loadAll}
        customerTags={customerTags}
        selectedCustomerTags={selectedCustomerTags}
        onSelectedCustomerTag={setSelectedCustomerTags}
        envOptions={envOptions}
        selectedEnvTypes={selectedEnvTypes}
        onSelectedEnvType={setSelectedEnvTypes}
      />

      <Paper className={classes.paper}>
        <Loader loading={loading} />

        {canViewCustomers() ? (
          <div className={classes.tableWrapper}>
            <Table className={classes.table}>
              <CustomerTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {showTags && (
                  <CommonTag
                    objectType="Customer"
                    saveTags={() => {}}
                    objectId={selectedCustomerId}
                    options={tagOptions}
                    setShowTags={setShowTags}
                  />
                )}

                {sortedPage.map((customer) => (
                  <TableRow key={customer.Id}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {customer.CustomerName}
                      </Typography>
                    </TableCell>
                    <TableCell>{customer.Id}</TableCell>
                    <TableCell>{customer.Subdomain}</TableCell>
                    <TableCell>{getOnboardDate(customer.Id)}</TableCell>
                    <TableCell>
                      {getLocalDate(customer.DeploymentDate)}
                    </TableCell>
                    <TableCell>{getLocalDate(customer.InsertDate)}</TableCell>
                    <TableCell>
                      {getDeploymentStatusChip(customer.DeploymentStateID)}
                    </TableCell>
                    <TableCell>{getEnvChip(customer.EnvType)}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleTagIconClick(customer.Id)}
                      >
                        <SellIcon color="primary" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTenantVersion(customer.Id)}
                        size="small"
                        color="info"
                      />
                    </TableCell>
                    <TableCell>
                      <CustomerMenu
                        moduleIds={customerModules[customer.Id] ?? []}
                        customerId={customer.Id}
                        stateId={customer.StateID}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    count={customers.length}
                    rowsPerPage={ROWS_PER_PAGE}
                    page={page}
                    onPageChange={handlePageChange}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        ) : (
          <h4 style={{ textAlign: 'center' }}>
            {CustomerConstants.CanNotViewCustomers}
          </h4>
        )}
      </Paper>
    </div>
  );
}
